import type {
    DaysSync, EngineVersion, LinearEngineParams, OptimizerGuard, SegmentOutcome, Setup,
    Thermometer, TypeSource,
} from '@/types/price-structure';

/**
 * Motores disponíveis. A escolha é por domínio: pôr um domínio no v4 não muda
 * nada nos outros, e voltar é só escolher v3 de novo.
 */
export const ENGINES: { value: EngineVersion; short: string; label: string; detail: string }[] = [
    {
        value: 'v3',
        short: 'v3 · Atual',
        label: 'v3 — curva pelo PMR',
        detail: 'topo = eCPM × agressividade, ajustado pelo PMR; curva geométrica',
    },
    {
        value: 'v4',
        short: 'v4 · Linear',
        label: 'v4 — linear pelo eCPM',
        detail: 'abertura = eCPM × multiplicador; degraus iguais até o chão; + 0,05 no fim; roda todo dia',
    },
];

/** Espelha LinearEngineParams do ms (models/schemas.py). */
export const DEFAULT_LINEAR_PARAMS: LinearEngineParams = {
    open_multiplier: 1.3,
    floor_fraction: 0.15,
    fallback: true,
    min_revenue: 1,
    min_distinct: 3,
    thermometer: 'first_step',
    target: 40,
    speed: 0.4,
    min_opportunities: 100,
    deadband: 0.05,
    min_rule: 0.5,
    max_rule: 800,
};

export const THERMOMETERS: { value: Thermometer; label: string; detail: string }[] = [
    {
        value: 'first_step',
        label: '1º degrau',
        detail: 'vendeu na abertura ÷ oportunidades — o "preço pedido" da ActiveView',
    },
    {
        value: 'viewable',
        label: 'Vendeu e visto',
        detail: 'vendeu com viewable ÷ oportunidades — a definição literal da ActiveView',
    },
];

/** Texto curto do motivo da regra, para a aba Regras. */
export const GUARD_LABEL: Record<OptimizerGuard, string> = {
    semente: 'semente',
    ajustado: 'ajustado',
    zona_morta: 'no alvo',
    sem_oportunidade: 'parado: sem tráfego',
    sem_venda: 'parado: sem venda',
    pouco_trafego: 'parado: pouco tráfego',
    piso: 'preso no piso',
    teto: 'preso no teto',
    sem_termometro: 'semente: sem termômetro',
};

/** US$ 1,00 — o corte de volume do v4. */
export const formatUsd = (value: number) =>
    `US$ ${value.toFixed(2).replace('.', ',')}`;

export const engineOf = (setup: { engine_version?: EngineVersion | null }): EngineVersion =>
    setup.engine_version ?? 'v3';

export const linearParamsOf = (setup: { engine_params?: LinearEngineParams | null }) => ({
    ...DEFAULT_LINEAR_PARAMS,
    ...(setup.engine_params ?? {}),
});

/** Resumo de uma linha dos parâmetros do v4, para card, tabela e tooltip. */
export function linearSummary(params: LinearEngineParams, steps: number) {
    const pct = Math.round(params.floor_fraction * 100);
    return (
        `semente eCPM × ${br(params.open_multiplier)}, abre na regra · chão ${pct}% da abertura · ` +
        `${steps} degraus${params.fallback ? ' + 0,05' : ''} · corrige todo dia pela taxa do GAM, ` +
        `alvo ${params.target}%`
    );
}

/** O v4 lê só ontem e roda todo dia, qualquer que seja o `days_sync`. */
export function windowLabel(setup: Pick<Setup, 'days_sync'> & { engine_version?: EngineVersion }) {
    const days = DAYS_SYNC.find((d) => d.value === setup.days_sync);
    if (engineOf(setup) === 'v4') {
        return { short: 'Ontem · diário', label: 'Dia anterior — roda todo dia' };
    }
    return { short: days?.short ?? '—', label: days?.label ?? '—' };
}

/**
 * Espelha engine/tuning.py do ms-price-structure. Mantenha em sincronia.
 *
 * Os fatores são numéricos, não texto: o modal de ajuda, o explicativo por bloco
 * e o campo de PMR alvo derivam deles, e fazer parse de string para isso seria
 * frágil demais.
 */
export const AGGRESSIVENESS = [
    { value: 1, short: '1 · Conservador', label: '1 — Conservador', topMultiplier: 1.15, band: 0.8, pmrTarget: 75 },
    { value: 2, short: '2 · Cauteloso', label: '2 — Cauteloso', topMultiplier: 1.35, band: 0.66, pmrTarget: 60 },
    { value: 3, short: '3 · Equilibrado', label: '3 — Equilibrado', topMultiplier: 1.55, band: 0.55, pmrTarget: 50 },
    { value: 4, short: '4 · Agressivo', label: '4 — Agressivo', topMultiplier: 1.75, band: 0.47, pmrTarget: 35 },
    { value: 5, short: '5 · Máximo', label: '5 — Máximo', topMultiplier: 2.0, band: 0.4, pmrTarget: 20 },
];

const br = (value: number, digits = 2) => value.toFixed(digits).replace('.', ',');

/** Texto do tooltip, derivado dos fatores — nunca digitado à mão. */
export const aggressivenessDetail = (level: (typeof AGGRESSIVENESS)[number]) =>
    `topo ${br(level.topMultiplier)}× · banda ${br(level.band)} · alvo PMR ${level.pmrTarget}%`;

/** Alvo de PMR que a agressividade aplica quando não há override. */
export function pmrTargetOf(aggressiveness: number): number {
    return AGGRESSIVENESS.find((a) => a.value === aggressiveness)?.pmrTarget ?? 0;
}

export const GRANULARITY = [
    { value: 1, short: '1 · Muito perto', label: '1 — Muito próximos', factor: 0.2, detail: 'salto na base = 20% do médio' },
    { value: 2, short: '2 · Perto', label: '2 — Próximos', factor: 0.4, detail: '40% do médio' },
    { value: 3, short: '3 · Médio', label: '3 — Médio', factor: 0.6, detail: '60% do médio' },
    { value: 4, short: '4 · Espaçado', label: '4 — Espaçados', factor: 0.8, detail: '80% do médio' },
    { value: 5, short: '5 · Uniforme', label: '5 — Uniforme', factor: 1.0, detail: 'espaçamento constante (o limite)' },
];

export const DAYS_SYNC: { value: DaysSync; short: string; label: string }[] = [
    { value: 1, short: 'Ontem', label: 'Ontem — roda todo dia' },
    { value: 3, short: '3 dias', label: 'Últimos 3 dias — roda a cada 3 dias' },
    { value: 7, short: '7 dias', label: 'Últimos 7 dias — roda a cada 7 dias' },
];

export const TYPE_SOURCE: { value: TypeSource; short: string; label: string }[] = [
    { value: 'id_post_wp', short: 'Post', label: 'Post do WordPress' },
    { value: 'utm_campaign', short: 'Campanha', label: 'Campanha (utm_campaign)' },
];

/** Versão enxuta para caber nas células da grade sem estourar a largura. */
export function toShortOptions<T extends { value: unknown; short: string; detail?: string }>(
    options: readonly T[],
) {
    return options.map((o) => ({ value: o.value as number | string, label: o.short, detail: o.detail }));
}

/** Idem, mas com o tooltip derivado dos fatores da agressividade. */
export function aggressivenessOptions() {
    return AGGRESSIVENESS.map((a) => ({
        value: a.value as number | string,
        label: a.short,
        detail: aggressivenessDetail(a),
    }));
}

export const OUTCOME_LABEL: Record<SegmentOutcome, string> = {
    written: 'Completo',
    degraded: 'Degradado',
    skipped_min_impressions: 'Sem volume',
    skipped_no_ecpm: 'Sem eCPM',
    skipped_no_pmr: 'Sem PMR',
    skipped_vocab_too_sparse: 'Vocabulário esparso',
};

export const OUTCOME_STYLE: Record<SegmentOutcome, string> = {
    written: 'bg-emerald-100 text-emerald-700',
    degraded: 'bg-amber-100 text-amber-700',
    skipped_min_impressions: 'bg-gray-100 text-gray-600',
    skipped_no_ecpm: 'bg-gray-100 text-gray-600',
    skipped_no_pmr: 'bg-gray-100 text-gray-600',
    skipped_vocab_too_sparse: 'bg-red-100 text-red-700',
};

export const DEFAULT_DIALS = {
    engine_version: 'v3' as EngineVersion,
    aggressiveness: 4,
    granularity: 3,
    type_source: 'id_post_wp' as TypeSource,
    min_impressions: 500,
    days_sync: 3 as DaysSync,
    steps_count: 20,
    pmr_target_override: null as number | null,
    engine_params: null as LinearEngineParams | null,
};

/**
 * O que muda ao trocar o motor de uma linha.
 *
 * No v4 a janela não é mais a cadência (ele roda todo dia): ela só define o
 * eCPM que serve de semente e de limite. O padrão é "ontem" — quem manda no
 * preço é o preenchimento de ontem, e um eCPM de ontem acompanha o mercado sem
 * arrastar média de uma semana. O corte de volume é US$ 1 de receita na janela.
 */
export const ENGINE_DEFAULTS: Record<EngineVersion, Partial<typeof DEFAULT_DIALS>> = {
    v3: {
        engine_version: 'v3',
        min_impressions: DEFAULT_DIALS.min_impressions,
        days_sync: DEFAULT_DIALS.days_sync,
        steps_count: DEFAULT_DIALS.steps_count,
        engine_params: null,
    },
    // No v4 o corte de volume é a receita (engine_params.min_revenue), não
    // impressões: 20 impressões de um bloco barato e de um caro não valem igual.
    v4: {
        engine_version: 'v4',
        min_impressions: 0,
        days_sync: 1,
        steps_count: 6,
        engine_params: { ...DEFAULT_LINEAR_PARAMS },
    },
};

/**
 * Normaliza a resposta de uma lista.
 *
 * Quando um backend está fora do ar, o dev server devolve o index.html com
 * status 200 — o que faz `data` virar string e qualquer `.map`/`.forEach`
 * derrubar a página inteira. Melhor renderizar vazio do que quebrar a app.
 */
export function asArray<T>(value: unknown): T[] {
    return Array.isArray(value) ? (value as T[]) : [];
}

export type Health = 'good' | 'warn' | 'bad';

/** Um eixo da avaliação, com o número que o classificou. */
export interface HealthFactor {
    label: string;
    level: Health;
    detail: string;
}

export interface HealthVerdict {
    level: Health;
    factors: HealthFactor[];
    /** Frase curta com o que puxou a nota para baixo. */
    summary: string;
}

// Limiares por eixo. Separados de propósito: avaliar os dois em conjunto com um
// AND fazia um único eixo levemente fora do limite derrubar tudo para "Ruim",
// sem dizer qual dos dois foi.
const DELIVERY_GOOD = 0.8;
const DELIVERY_WARN = 0.5;
const COVERAGE_GOOD = 0.95;
const COVERAGE_WARN = 0.85;

const worst = (levels: Health[]): Health =>
    levels.includes('bad') ? 'bad' : levels.includes('warn') ? 'warn' : 'good';

/**
 * Traduz o resultado da simulação num sinal acionável — e explica o porquê.
 *
 * Dois eixos independentes: quanto dos steps pedidos foi entregue, e quantos
 * segmentos chegaram a gerar granulação. A nota final é a pior das duas, e cada
 * uma carrega o número que a classificou para o operador não ter que adivinhar.
 */
export function evaluateHealth(
    avgDelivered: number,
    requested: number,
    found: number,
    skipped: number,
): HealthVerdict {
    if (!found) {
        return {
            level: 'bad',
            factors: [],
            summary: 'Nenhum segmento passou do filtro de impressões mínimas.',
        };
    }

    const delivery = requested > 0 ? avgDelivered / requested : 0;
    const coverage = (found - skipped) / found;
    const pct = (value: number) => `${(value * 100).toFixed(0)}%`;

    const deliveryLevel: Health =
        delivery >= DELIVERY_GOOD ? 'good' : delivery >= DELIVERY_WARN ? 'warn' : 'bad';
    const coverageLevel: Health =
        coverage >= COVERAGE_GOOD ? 'good' : coverage >= COVERAGE_WARN ? 'warn' : 'bad';

    const factors: HealthFactor[] = [
        {
            label: 'Entrega de steps',
            level: deliveryLevel,
            detail:
                `média de ${avgDelivered} dos ${requested} pedidos (${pct(delivery)}) — ` +
                `bom acima de ${pct(DELIVERY_GOOD)}, atenção acima de ${pct(DELIVERY_WARN)}`,
        },
        {
            label: 'Cobertura dos segmentos',
            level: coverageLevel,
            detail:
                `${found - skipped} de ${found} geraram granulação (${pct(coverage)}) — ` +
                `bom acima de ${pct(COVERAGE_GOOD)}, atenção acima de ${pct(COVERAGE_WARN)}`,
        },
    ];

    const problems = factors.filter((f) => f.level !== 'good');
    const summary = problems.length
        ? problems.map((f) => f.label.toLowerCase()).join(' e ') + ' abaixo do esperado'
        : 'Entrega e cobertura dentro do esperado.';

    return { level: worst([deliveryLevel, coverageLevel]), factors, summary };
}

/** Atalho para quem só precisa da cor. */
export function healthOf(
    avgDelivered: number,
    requested: number,
    found: number,
    skipped: number,
): Health {
    return evaluateHealth(avgDelivered, requested, found, skipped).level;
}

export const HEALTH_STYLE: Record<Health, string> = {
    good: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    warn: 'bg-amber-100 text-amber-700 border-amber-200',
    bad: 'bg-red-100 text-red-700 border-red-200',
};

export const HEALTH_LABEL: Record<Health, string> = {
    good: 'Saudável',
    warn: 'Atenção',
    bad: 'Ruim',
};
